import { getOpenAbortModalEvent } from '@ui-common/components/Modal'
import { usePersistingActor } from '@ui-common/hooks'
import { GlobalServicesContext } from '@ui-common/state'
import { commonExit, getLanguage } from '@ui-common/utils'
import { shallowEqual, useSelector } from '@xstate/react'
import { use, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { ActorRefFrom, SnapshotFrom } from 'xstate'

import { getFallbackExitUrls } from '../../utils/helpers'
import { LoanOfferAnalyticsContext } from '../analytics/analyticsContext'
import { FormContextValue } from '../types'
import { createWizardMachine, WizardContext, WizardMachineType } from './wizardMachine'

interface UseWizardLogicReturn extends FormContextValue {
  actor: ActorRefFrom<WizardMachineType>
}

const selectValues = ({ context: { user, loanOffer, exitUrls }, value }: SnapshotFrom<WizardMachineType>) => ({
  user,
  loanOffer,
  step: value,
  exitUrls,
})

export const useWizardLogic = (initialContext: WizardContext): UseWizardLogicReturn => {
  const intl = useIntl()
  const { analyticsService } = use(LoanOfferAnalyticsContext)
  const { modalService } = use(GlobalServicesContext)
  const modalSend = modalService.send

  const wizardMachine = createWizardMachine()
  const actor = usePersistingActor<WizardMachineType>(wizardMachine, undefined, undefined, initialContext)
  const send = actor.send

  const { user, loanOffer, step, exitUrls } = useSelector(actor, selectValues, shallowEqual)

  const exit = async () => {
    analyticsService.sendCustomEvent(step, 'AbortButton')
    await commonExit((exitUrls ?? getFallbackExitUrls()).successUrl[getLanguage()])
  }

  return {
    actor,
    user,
    step,
    loanOffer,
    proceed: useCallback(() => send({ type: 'NEXT_STEP', data: undefined }), [send]),
    goBack: useCallback(() => send({ type: 'PREVIOUS_STEP' }), [send]),
    goBackToStatus: useCallback(() => send({ type: 'BACK_TO_STATUS' }), [send]),
    exit,
    exitWithConfirmDialog: () => {
      modalSend(getOpenAbortModalEvent(() => exit(), intl))
    },
  }
}
